// ─── Il pannello a fianco del calendario ─────────────────────────────────────
//
// 🗣️ **SCELTA SUA, del 16/09/2026**, fra tre disposizioni che gli ho fatto
// vedere disegnate: *«vai con la C»* — il pannello agganciato a destra, invece
// del foglio in mezzo allo schermo.
//
// ⭐⭐ **NON È UN MODALE, ED È TUTTO IL PUNTO.** Niente `showModal()`, niente
// fondale scuro sul computer: il calendario resta **visibile e cliccabile**
// accanto. ⇒ Si passa da una partita all'altra senza chiudere niente, si
// trascina uno slot mentre una scheda è aperta, si vede cosa c'è intorno all'ora
// che si sta sistemando.
// ⚖️ È la ragione per cui questo componente esiste invece di un `<dialog>`: un
//   `<dialog>` aperto con `showModal()` rende inerte tutto il resto della
//   pagina — cioè toglierebbe esattamente la cosa per cui è stato scelto.
//
// 🚨 E quello che il browser regalava col modale, qui va rifatto a mano, perché
// niente lo dice al posto nostro:
// · `Esc` chiude — la scorciatoia che tutti provano per prima;
// · il fuoco entra nel pannello quando si apre, o chi naviga da tastiera resta
//   con le mani sul calendario mentre a schermo è cambiata un'altra cosa;
// · ⛔ ma il fuoco **non si ingabbia**: sarebbe un modale con un altro nome, e
//   il Tab deve poter tornare sul calendario che è rimasto lì apposta.
//
// 📱 Sotto i 60rem il posto per stare a fianco non c'è: là il pannello copre la
// pagina e torna il velo, che a quel punto è giusto — non c'è nessun calendario
// da guardare dietro.

import { useEffect, useRef } from 'react';

export function Pannello({
  etichetta,
  chiudi,
  children,
}: {
  /** Come si chiama questo pannello per chi non lo vede (§ 03.6). */
  etichetta: string;
  chiudi(): void;
  children: React.ReactNode;
}): React.JSX.Element {
  const riquadro = useRef<HTMLElement>(null);

  /**
   * ⚠️ La chiusura passa da un riferimento e non dalle dipendenze dell'effetto:
   * chi ci chiama scrive `chiudi={() => setScheda(null)}`, cioè una funzione
   * nuova a ogni disegno. Con quella nelle dipendenze, l'ascolto della tastiera
   * si staccherebbe e riattaccherebbe a ogni tasto premuto dentro il pannello.
   */
  const ultimaChiusura = useRef(chiudi);
  ultimaChiusura.current = chiudi;

  useEffect(() => {
    riquadro.current?.focus({ preventScroll: true });
    function allaTastiera(e: KeyboardEvent): void {
      if (e.key === 'Escape') ultimaChiusura.current();
    }
    document.addEventListener('keydown', allaTastiera);
    return () => document.removeEventListener('keydown', allaTastiera);
  }, []);

  return (
    <>
      {/* 📱 Il velo esiste solo sotto i 60rem: il CSS lo tiene nascosto sopra,
          dove coprirebbe il calendario che questo pannello vuole lasciare in
          vista. È un bottone vero perché chiude, e chiudere è un'azione. */}
      <button type="button" className="velo-pannello" aria-label="Chiudi" tabIndex={-1} onClick={chiudi} />
      <aside className="pannello" aria-label={etichetta} ref={riquadro} tabIndex={-1}>
        {children}
      </aside>
    </>
  );
}

/** La testata del pannello: quello che si sta guardando, e la via d'uscita. */
export function TestaPannello({
  titolo,
  sotto,
  targhetta,
  chiudi,
}: {
  titolo: React.ReactNode;
  sotto?: React.ReactNode;
  targhetta?: string;
  chiudi(): void;
}): React.JSX.Element {
  return (
    <header className="testa-pannello">
      <div className="quale">
        <h2>
          {titolo}
          {targhetta !== undefined && <span className="targhetta">{targhetta}</span>}
        </h2>
        {sotto !== undefined && <p className="tenue piccolo">{sotto}</p>}
      </div>
      <button type="button" className="chiudi-pannello" aria-label="Chiudi" onClick={chiudi}>
        ✕
      </button>
    </header>
  );
}
